import React, { useState, useCallback } from 'react';
import { formatPickDisplay } from './RosterPicker';

// Two-team trade builder for the analyst review wizard — light-mode shell design.

const ff = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Helvetica Neue', Arial, sans-serif";
const DYNASTY_POSITIONS = ['QB', 'RB', 'WR', 'TE'];

const ktcOf = (p, isSF) => (isSF ? (p.ktcValueSf || 0) : (p.ktcValue1qb || 0));

const sortByKtc = (players, isSF) =>
  [...players].sort((a, b) => {
    const diff = ktcOf(b, isSF) - ktcOf(a, isSF);
    if (diff !== 0) return diff;
    return (a.name || '').localeCompare(b.name || '');
  });

const userAvatarUrl = (avatar) => {
  if (!avatar) return null;
  if (typeof avatar === 'string' && avatar.startsWith('http')) return avatar;
  return `https://sleepercdn.com/avatars/thumbs/${avatar}`;
};
const playerAvatarUrl = (id) => (id && /^\d+$/.test(String(id))
  ? `https://sleepercdn.com/content/nfl/players/thumb/${id}.jpg`
  : null);

const Avatar = ({ src, name, size = 32, dark = false }) => {
  const [errored, setErrored] = useState(false);
  const show = src && !errored;
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: show ? '#F5F5F7' : '#F5F5F7',
      border: `1px solid #EBEBED`,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      flexShrink: 0, overflow: 'hidden',
      fontSize: Math.floor(size * 0.42), fontWeight: 700, color: '#86868B',
    }}>
      {show
        ? <img src={src} alt="" onError={() => setErrored(true)} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        : (name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

// ── Pick override modal ─────────────────────────────────────────────────────
const PickOverridePicker = ({ pick, numTeams, onSave, onClose, onReset }) => {
  if (!pick) return null;
  const slotCount = Math.max(8, Math.min(20, numTeams || 12));
  const tiers = ['early', 'mid', 'late'];
  const cur = pick.override || null;
  const baseDisplay = formatPickDisplay({ ...pick, override: null });
  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', overflowY: 'auto', fontFamily: ff }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#FFF', borderRadius: '20px', border: '1px solid #EBEBED', boxShadow: '0 20px 60px rgba(0,0,0,0.12)', maxWidth: '480px', width: '100%', padding: '28px', position: 'relative' }}>
        <button onClick={onClose} aria-label="Close"
          style={{ position: 'absolute', top: '14px', right: '14px', width: '30px', height: '30px', borderRadius: '50%', border: 'none', background: '#F5F5F7', color: '#6E6E73', fontSize: '16px', cursor: 'pointer', fontFamily: ff }}>×</button>
        <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '2px', textTransform: 'uppercase', color: '#86868B', marginBottom: '6px' }}>Refine pick</div>
        <div style={{ fontSize: '22px', fontWeight: 700, letterSpacing: '-0.02em', color: '#1D1D1F', marginBottom: '4px' }}>{baseDisplay}</div>
        <div style={{ fontSize: '13px', color: '#6E6E73', marginBottom: '22px' }}>Refine where this pick lands. Default uses our projection.</div>

        <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#86868B', marginBottom: '10px' }}>Tier</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '22px' }}>
          {tiers.map((t) => {
            const active = cur?.type === 'tier' && cur.value === t;
            return (
              <button key={t} onClick={() => onSave({ type: 'tier', value: t })}
                style={{ padding: '14px', borderRadius: '12px', cursor: 'pointer', background: active ? '#1D1D1F' : '#F5F5F7', color: active ? '#FFF' : '#1D1D1F', border: 'none', fontSize: '14px', fontWeight: 600, fontFamily: ff, textTransform: 'capitalize' }}>{t}</button>
            );
          })}
        </div>

        <div style={{ fontSize: '10px', fontWeight: 600, letterSpacing: '2px', textTransform: 'uppercase', color: '#86868B', marginBottom: '10px' }}>Or exact slot</div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(6, slotCount)}, 1fr)`, gap: '6px', marginBottom: '22px' }}>
          {Array.from({ length: slotCount }, (_, i) => i + 1).map((slot) => {
            const active = cur?.type === 'exact' && cur.value === slot;
            return (
              <button key={slot} onClick={() => onSave({ type: 'exact', value: slot })}
                style={{ padding: '12px 0', borderRadius: '10px', cursor: 'pointer', background: active ? '#1D1D1F' : '#F5F5F7', color: active ? '#FFF' : '#1D1D1F', border: 'none', fontSize: '13px', fontWeight: 600, fontFamily: ff }}>{pick.round}.{String(slot).padStart(2, '0')}</button>
            );
          })}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <button onClick={onReset} disabled={!cur}
            style={{ background: 'none', border: 'none', cursor: cur ? 'pointer' : 'default', color: cur ? '#6E6E73' : '#D2D2D7', fontFamily: ff, fontSize: '13px', padding: '10px 4px' }}>Reset to default</button>
          <button onClick={onClose}
            style={{ background: '#1D1D1F', color: '#FFF', border: 'none', borderRadius: '980px', padding: '12px 24px', fontFamily: ff, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>Done</button>
        </div>
      </div>
    </div>
  );
};

// ── Main two-team picker ────────────────────────────────────────────────────
const TradeBuilder = ({ myTeam, theirTeam, myGives, theirGives, onChangeMy, onChangeTheir, numTeams, isSuperFlex, draftedSeasons = [] }) => {
  const [activeTab, setActiveTab] = useState('QB');
  const [editingPick, setEditingPick] = useState(null);

  const toggle = useCallback((side, type, item) => {
    const cur = side === 'my' ? myGives : theirGives;
    const setter = side === 'my' ? onChangeMy : onChangeTheir;
    const arr = cur[type];
    const exists = arr.some((x) => x.id === item.id);
    setter({ ...cur, [type]: exists ? arr.filter((x) => x.id !== item.id) : [...arr, item] });
  }, [myGives, theirGives, onChangeMy, onChangeTheir]);

  const setPickOverride = useCallback((side, pickId, override) => {
    const cur = side === 'my' ? myGives : theirGives;
    const setter = side === 'my' ? onChangeMy : onChangeTheir;
    setter({ ...cur, picks: cur.picks.map((p) => (p.id === pickId ? { ...p, override } : p)) });
    setEditingPick((e) => (e ? { ...e, pick: { ...e.pick, override } } : e));
  }, [myGives, theirGives, onChangeMy, onChangeTheir]);

  const isSelected = (side, type, item) =>
    (side === 'my' ? myGives : theirGives)[type].some((x) => x.id === item.id);

  const getByPos = (team, pos) => sortByKtc((team?.players || []).filter((p) => p.position === pos), isSuperFlex);

  const selectedCountForTab = (tab) => {
    if (tab === 'PICKS') return myGives.picks.length + theirGives.picks.length;
    return [...myGives.players, ...theirGives.players].filter((p) => p.position === tab).length;
  };

  const renderPlayer = (p, side) => {
    const sel = isSelected(side, 'players', p);
    return (
      <div
        key={p.id}
        onClick={() => toggle(side, 'players', p)}
        style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '8px 10px', borderRadius: '10px', cursor: 'pointer', marginBottom: '4px',
          background: sel ? '#1D1D1F' : '#F5F5F7',
          border: `1px solid ${sel ? 'transparent' : '#EBEBED'}`,
          transition: 'background 0.12s',
        }}
      >
        <Avatar src={playerAvatarUrl(p.id)} name={p.name} size={28} />
        <span style={{ fontSize: '13px', fontWeight: 500, flex: 1, color: sel ? '#FFF' : '#1D1D1F', fontFamily: ff, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
        {p.team && <span style={{ fontSize: '11px', color: sel ? 'rgba(255,255,255,0.5)' : '#86868B', fontFamily: ff, flexShrink: 0 }}>{p.team}</span>}
        {sel && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', flexShrink: 0 }}>✓</span>}
      </div>
    );
  };

  const renderPick = (pk, side) => {
    const sel = isSelected(side, 'picks', pk);
    const selectedPick = sel ? (side === 'my' ? myGives : theirGives).picks.find((p) => p.id === pk.id) : pk;
    const display = formatPickDisplay(selectedPick || pk);
    return (
      <div
        key={pk.id}
        onClick={() => toggle(side, 'picks', pk)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '10px 12px', borderRadius: '10px', cursor: 'pointer', marginBottom: '4px',
          background: sel ? '#1D1D1F' : '#F5F5F7',
          border: `1px solid ${sel ? 'transparent' : '#EBEBED'}`,
        }}
      >
        <span style={{ fontSize: '13px', flex: 1, color: sel ? '#FFF' : '#1D1D1F', fontFamily: ff }}>{display}</span>
        {sel && (
          <button
            onClick={(e) => { e.stopPropagation(); setEditingPick({ pick: selectedPick || pk, side }); }}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.8)', borderRadius: '6px', padding: '3px 8px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: ff }}
          >Set</button>
        )}
        {sel && <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.6)' }}>✓</span>}
      </div>
    );
  };

  if (!myTeam || !theirTeam) return null;

  return (
    <div>
      {/* Position tabs */}
      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
        {[...DYNASTY_POSITIONS, 'PICKS'].map((tab) => {
          const count = selectedCountForTab(tab);
          const active = activeTab === tab;
          return (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '7px 18px', borderRadius: '980px', border: `1px solid ${active ? '#1D1D1F' : '#EBEBED'}`,
                background: active ? '#1D1D1F' : '#FFF',
                color: active ? '#FFF' : '#6E6E73',
                fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                transition: 'all 0.15s', fontFamily: ff, position: 'relative',
              }}
            >
              {tab}
              {count > 0 && (
                <span style={{
                  position: 'absolute', top: '-5px', right: '-5px',
                  background: active ? '#FFF' : '#1D1D1F',
                  color: active ? '#1D1D1F' : '#FFF',
                  borderRadius: '50%', width: '16px', height: '16px',
                  fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontWeight: 700,
                }}>{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Two-column player/pick grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
        {[['my', myTeam], ['their', theirTeam]].map(([side, team]) => {
          const rawPicks = (team.picks || []).filter((pk) => !draftedSeasons.includes(String(pk.season)));
          const items = activeTab === 'PICKS' ? rawPicks : getByPos(team, activeTab);
          return (
            <div key={side}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                <Avatar src={userAvatarUrl(team.avatar)} name={team.ownerName} size={30} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: '#1D1D1F', letterSpacing: '-0.01em', fontFamily: ff, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {team.ownerName}
                  </div>
                  <div style={{ fontSize: '11px', color: '#86868B', fontFamily: ff }}>
                    {side === 'my' ? 'Your team' : 'Their team'}
                  </div>
                </div>
              </div>
              {items.length === 0
                ? <div style={{ color: '#D2D2D7', fontSize: '13px', padding: '8px 0', fontFamily: ff }}>None</div>
                : items.map((item) => activeTab === 'PICKS' ? renderPick(item, side) : renderPlayer(item, side))
              }
            </div>
          );
        })}
      </div>

      <PickOverridePicker
        pick={editingPick?.pick}
        numTeams={numTeams || 12}
        onClose={() => setEditingPick(null)}
        onSave={(override) => editingPick && setPickOverride(editingPick.side, editingPick.pick.id, override)}
        onReset={() => editingPick && setPickOverride(editingPick.side, editingPick.pick.id, null)}
      />
    </div>
  );
};

export default TradeBuilder;
